import React, { useState, useEffect, useCallback } from 'react';
import { PageHeader } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Search,
  RefreshCw,
  AlertTriangle,
  AlertCircle,
  Info,
  Bug,
  XCircle,
  CheckCircle,
  MoreHorizontal,
  Eye,
  Trash2,
  CheckCheck,
  Clock,
  Globe,
  Server,
  Code,
  Zap,
  Filter,
  ChevronLeft,
  ChevronRight,
  Download,
  Trash,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

// Level badge colors and icons
const levelConfig = {
  debug: { color: 'bg-slate-100 text-slate-700 border-slate-200', icon: Bug, label: 'Debug' },
  info: { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Info, label: 'Info' },
  warning: { color: 'bg-amber-100 text-amber-700 border-amber-200', icon: AlertTriangle, label: 'Warning' },
  error: { color: 'bg-red-100 text-red-700 border-red-200', icon: AlertCircle, label: 'Error' },
  critical: { color: 'bg-red-200 text-red-800 border-red-300', icon: XCircle, label: 'Critical' },
};

// Source icons
const sourceConfig = {
  backend: { icon: Server, label: 'Backend' },
  frontend: { icon: Globe, label: 'Frontend' },
  api: { icon: Code, label: 'API' },
  scheduled_task: { icon: Clock, label: 'Scheduled Task' },
  webhook: { icon: Zap, label: 'Webhook' },
  integration: { icon: Code, label: 'Integration' },
  system: { icon: Server, label: 'System' },
};

function LevelBadge({ level }) {
  const config = levelConfig[level] || levelConfig.error;
  const Icon = config.icon;
  
  return (
    <Badge variant="outline" className={`${config.color} gap-1`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
}

function SourceBadge({ source }) {
  const config = sourceConfig[source] || sourceConfig.backend;
  const Icon = config.icon;
  
  return (
    <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200 gap-1">
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
}

function StatCard({ title, value, description, icon: Icon, color }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className={`h-4 w-4 ${color}`} />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {description && (
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        )}
      </CardContent>
    </Card>
  );
}

export default function AdminErrorLogs() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  
  // Filters
  const [search, setSearch] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [resolvedFilter, setResolvedFilter] = useState('all');
  
  // Pagination
  const [page, setPage] = useState(0);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  
  // Selection
  const [selectedLogs, setSelectedLogs] = useState([]);
  
  // Dialogs
  const [detailsDialog, setDetailsDialog] = useState({ open: false, log: null });
  const [resolveDialog, setResolveDialog] = useState({ open: false, logIds: [], notes: '' });

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/v1/admin/error-logs/stats`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: (page * pageSize).toString(),
        limit: pageSize.toString(),
        sort_by: 'created_at',
        sort_order: 'desc'
      });
      
      if (search) params.append('search', search);
      if (levelFilter !== 'all') params.append('level', levelFilter);
      if (sourceFilter !== 'all') params.append('source', sourceFilter);
      if (resolvedFilter !== 'all') params.append('is_resolved', resolvedFilter === 'resolved' ? 'true' : 'false');
      
      const response = await fetch(`${API_URL}/api/v1/admin/error-logs?${params}`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (data.success) {
        setLogs(data.data);
        setTotal(data.meta.total);
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Failed to fetch error logs');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, levelFilter, sourceFilter, resolvedFilter]);

  useEffect(() => {
    fetchStats();
    fetchLogs();
  }, [fetchStats, fetchLogs]);

  const handleRefresh = () => {
    fetchStats();
    fetchLogs();
    toast.success('Logs refreshed');
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedLogs(logs.map(log => log.id));
    } else {
      setSelectedLogs([]);
    }
  };

  const handleSelectLog = (logId, checked) => {
    if (checked) {
      setSelectedLogs(prev => [...prev, logId]);
    } else {
      setSelectedLogs(prev => prev.filter(id => id !== logId));
    }
  };

  const handleResolve = async (logIds, notes = '') => {
    try {
      const response = await fetch(`${API_URL}/api/v1/admin/error-logs/bulk/resolve`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(logIds)
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`Resolved ${data.data.resolved_count} error(s)`);
        setSelectedLogs([]);
        setResolveDialog({ open: false, logIds: [], notes: '' });
        fetchStats();
        fetchLogs();
      }
    } catch (error) {
      toast.error('Failed to resolve errors');
    }
  };

  const handleDelete = async (logIds) => {
    if (!confirm(`Are you sure you want to delete ${logIds.length} error log(s)?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/v1/admin/error-logs/bulk/delete`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(logIds)
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`Deleted ${data.data.deleted_count} error(s)`);
        setSelectedLogs([]);
        fetchStats();
        fetchLogs();
      }
    } catch (error) {
      toast.error('Failed to delete errors');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return `${Math.floor(diff / 86400000)}d ago`;
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="p-6 space-y-6" data-testid="admin-error-logs-page">
      <PageHeader
        title="Error Logs"
        description="Monitor and manage application errors and exceptions"
      >
        <Button variant="outline" onClick={handleRefresh} data-testid="refresh-logs-btn">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </PageHeader>

      {/* Stats Overview */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {statsLoading ? (
          <>
            {[1, 2, 3, 4].map(i => (
              <Card key={i}>
                <CardHeader className="pb-2">
                  <Skeleton className="h-4 w-24" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-8 w-16" />
                </CardContent>
              </Card>
            ))}
          </>
        ) : stats && (
          <>
            <StatCard
              title="Total Errors"
              value={stats.total_count}
              icon={AlertCircle}
              color="text-slate-500"
            />
            <StatCard
              title="Unresolved"
              value={stats.unresolved_count}
              description="Needs attention"
              icon={AlertTriangle}
              color="text-amber-500"
            />
            <StatCard
              title="Last 24 Hours"
              value={stats.recent_errors}
              description="Recent activity"
              icon={Clock}
              color="text-blue-500"
            />
            <StatCard
              title="Critical"
              value={stats.by_level?.critical || 0}
              description="High priority"
              icon={XCircle}
              color="text-red-500"
            />
          </>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search errors..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="search-errors-input"
                />
              </div>
            </div>
            
            <Select value={levelFilter} onValueChange={setLevelFilter}>
              <SelectTrigger className="w-[140px]" data-testid="level-filter">
                <SelectValue placeholder="Level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Levels</SelectItem>
                <SelectItem value="debug">Debug</SelectItem>
                <SelectItem value="info">Info</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="error">Error</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="w-[150px]" data-testid="source-filter">
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="backend">Backend</SelectItem>
                <SelectItem value="frontend">Frontend</SelectItem>
                <SelectItem value="api">API</SelectItem>
                <SelectItem value="scheduled_task">Scheduled Task</SelectItem>
                <SelectItem value="webhook">Webhook</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={resolvedFilter} onValueChange={setResolvedFilter}>
              <SelectTrigger className="w-[140px]" data-testid="resolved-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="unresolved">Unresolved</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions */}
      {selectedLogs.length > 0 && (
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">
                {selectedLogs.length} error(s) selected
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setResolveDialog({ open: true, logIds: selectedLogs, notes: '' })}
                  data-testid="bulk-resolve-btn"
                >
                  <CheckCheck className="h-4 w-4 mr-1" />
                  Resolve Selected
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleDelete(selectedLogs)}
                  data-testid="bulk-delete-btn"
                >
                  <Trash className="h-4 w-4 mr-1" />
                  Delete Selected
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Error Logs Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]">
                  <Checkbox
                    checked={selectedLogs.length === logs.length && logs.length > 0}
                    onCheckedChange={handleSelectAll}
                    data-testid="select-all-checkbox"
                  />
                </TableHead>
                <TableHead className="w-[100px]">Level</TableHead>
                <TableHead className="w-[120px]">Source</TableHead>
                <TableHead>Error</TableHead>
                <TableHead className="w-[100px]">Occurrences</TableHead>
                <TableHead className="w-[140px]">Last Seen</TableHead>
                <TableHead className="w-[100px]">Status</TableHead>
                <TableHead className="w-[60px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-4 w-4" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-16" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-20" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-full" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-8" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-16" /></TableCell>
                    <TableCell><Skeleton className="h-8 w-8" /></TableCell>
                  </TableRow>
                ))
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="h-32 text-center">
                    <div className="flex flex-col items-center justify-center text-muted-foreground">
                      <CheckCircle className="h-8 w-8 mb-2 text-green-500" />
                      <p className="font-medium">No errors found</p>
                      <p className="text-sm">Your application is running smoothly!</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow
                    key={log.id}
                    className={log.is_resolved ? 'opacity-60' : ''}
                    data-testid={`error-row-${log.id}`}
                  >
                    <TableCell>
                      <Checkbox
                        checked={selectedLogs.includes(log.id)}
                        onCheckedChange={(checked) => handleSelectLog(log.id, checked)}
                      />
                    </TableCell>
                    <TableCell>
                      <LevelBadge level={log.level} />
                    </TableCell>
                    <TableCell>
                      <SourceBadge source={log.source} />
                    </TableCell>
                    <TableCell>
                      <div className="max-w-md">
                        <p className="font-medium text-sm truncate" title={log.message}>
                          {log.error_type && (
                            <span className="text-muted-foreground">{log.error_type}: </span>
                          )}
                          {log.message}
                        </p>
                        {log.request_path && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {log.request_method} {log.request_path}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="font-mono">
                        {log.occurrence_count}x
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground" title={formatDate(log.last_seen)}>
                        {formatRelativeTime(log.last_seen)}
                      </span>
                    </TableCell>
                    <TableCell>
                      {log.is_resolved ? (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Resolved
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          Open
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`error-actions-${log.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setDetailsDialog({ open: true, log })}>
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          {!log.is_resolved && (
                            <DropdownMenuItem onClick={() => handleResolve([log.id])}>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Mark Resolved
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={() => handleDelete([log.id])}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {total > pageSize && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <p className="text-sm text-muted-foreground">
                Showing {page * pageSize + 1} to {Math.min((page + 1) * pageSize, total)} of {total} errors
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Error Details Dialog */}
      <Dialog open={detailsDialog.open} onOpenChange={(open) => setDetailsDialog({ open, log: null })}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Error Details
              {detailsDialog.log && <LevelBadge level={detailsDialog.log.level} />}
            </DialogTitle>
            <DialogDescription>
              Full information about this error
            </DialogDescription>
          </DialogHeader>
          
          {detailsDialog.log && (
            <ScrollArea className="max-h-[60vh]">
              <div className="space-y-4 pr-4">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Error Type</Label>
                    <p className="font-mono text-sm">{detailsDialog.log.error_type || 'Unknown'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Source</Label>
                    <p className="text-sm capitalize">{detailsDialog.log.source}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">First Seen</Label>
                    <p className="text-sm">{formatDate(detailsDialog.log.first_seen)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Last Seen</Label>
                    <p className="text-sm">{formatDate(detailsDialog.log.last_seen)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Occurrences</Label>
                    <p className="text-sm font-bold">{detailsDialog.log.occurrence_count}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Status</Label>
                    <p className="text-sm">{detailsDialog.log.is_resolved ? 'Resolved' : 'Open'}</p>
                  </div>
                </div>

                {/* Message */}
                <div>
                  <Label className="text-xs text-muted-foreground">Error Message</Label>
                  <p className="mt-1 text-sm bg-slate-50 p-3 rounded-md font-mono break-all">
                    {detailsDialog.log.message}
                  </p>
                </div>

                {/* Request Info */}
                {detailsDialog.log.request_path && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Request</Label>
                    <div className="mt-1 bg-slate-50 p-3 rounded-md text-sm font-mono">
                      <p>{detailsDialog.log.request_method} {detailsDialog.log.request_path}</p>
                      {detailsDialog.log.request_ip && (
                        <p className="text-muted-foreground">IP: {detailsDialog.log.request_ip}</p>
                      )}
                      {detailsDialog.log.user_agent && (
                        <p className="text-muted-foreground text-xs truncate">
                          UA: {detailsDialog.log.user_agent}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Location */}
                {(detailsDialog.log.module || detailsDialog.log.function) && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Location</Label>
                    <p className="mt-1 text-sm font-mono bg-slate-50 p-3 rounded-md">
                      {detailsDialog.log.module}
                      {detailsDialog.log.function && `:${detailsDialog.log.function}`}
                      {detailsDialog.log.line_number && `:${detailsDialog.log.line_number}`}
                    </p>
                  </div>
                )}

                {/* Stack Trace */}
                {detailsDialog.log.stack_trace && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Stack Trace</Label>
                    <pre className="mt-1 text-xs bg-slate-900 text-slate-100 p-3 rounded-md overflow-x-auto whitespace-pre-wrap">
                      {detailsDialog.log.stack_trace}
                    </pre>
                  </div>
                )}

                {/* Context */}
                {detailsDialog.log.context && Object.keys(detailsDialog.log.context).length > 0 && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Context</Label>
                    <pre className="mt-1 text-xs bg-slate-50 p-3 rounded-md overflow-x-auto">
                      {JSON.stringify(detailsDialog.log.context, null, 2)}
                    </pre>
                  </div>
                )}

                {/* Resolution Info */}
                {detailsDialog.log.is_resolved && (
                  <div className="border-t pt-4">
                    <Label className="text-xs text-muted-foreground">Resolution</Label>
                    <div className="mt-1 bg-green-50 p-3 rounded-md text-sm">
                      <p>Resolved at: {formatDate(detailsDialog.log.resolved_at)}</p>
                      {detailsDialog.log.resolved_by && (
                        <p>Resolved by: {detailsDialog.log.resolved_by}</p>
                      )}
                      {detailsDialog.log.resolution_notes && (
                        <p className="mt-2">Notes: {detailsDialog.log.resolution_notes}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          )}
          
          <DialogFooter>
            {detailsDialog.log && !detailsDialog.log.is_resolved && (
              <Button onClick={() => {
                handleResolve([detailsDialog.log.id]);
                setDetailsDialog({ open: false, log: null });
              }}>
                <CheckCircle className="h-4 w-4 mr-2" />
                Mark Resolved
              </Button>
            )}
            <Button variant="outline" onClick={() => setDetailsDialog({ open: false, log: null })}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Resolve Dialog */}
      <Dialog open={resolveDialog.open} onOpenChange={(open) => setResolveDialog({ open, logIds: [], notes: '' })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve Errors</DialogTitle>
            <DialogDescription>
              Mark {resolveDialog.logIds.length} error(s) as resolved
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Resolution Notes (optional)</Label>
              <Textarea
                placeholder="Add any notes about the resolution..."
                value={resolveDialog.notes}
                onChange={(e) => setResolveDialog(prev => ({ ...prev, notes: e.target.value }))}
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveDialog({ open: false, logIds: [], notes: '' })}>
              Cancel
            </Button>
            <Button onClick={() => handleResolve(resolveDialog.logIds, resolveDialog.notes)}>
              <CheckCircle className="h-4 w-4 mr-2" />
              Resolve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
